import {AfterViewInit, Component, OnInit} from '@angular/core';
import {Select, Store} from '@ngxs/store';
import {AuthState} from '../shared/states/auth/auth.state';
import {Observable} from 'rxjs/internal/Observable';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {LockSystem, Logout} from '../shared/states/auth/auth.actions';
import {TokensEnum} from '../shared/interfaces/TokensEnum';
import {CookiesService} from '../shared/services/cookies.service';
import {LOCK_TIME, LOGOUT_TIME} from '../shared/constants/constants';
import { PharmacyState } from '../shared/states/pharmacy/pharmacy.state';
import { IPharmacy } from '../shared/interfaces/pharmacy.interface';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss'],
})
export class BaseComponent implements OnInit, AfterViewInit {
  showResetPinDialog: boolean;
  intervalObserver: any;
  focusPin: boolean;
  hasPharmacy: boolean;

  @Select(AuthState.lockScreen) lockScreen$: Observable<boolean>;
  @Select(PharmacyState.currentPharmacy) currentPharmacy$: Observable<IPharmacy>;
  @Select(AuthState.activeUserIsLoggedInUser) activeUserIsLoggedInUser$: Observable<any>;

  constructor(
    private store: Store,
    private router: Router,
    private cookiesService: CookiesService,
  ) {
  }

  ngOnInit(): void {
    let idleMinutes = 0;
    const locked = JSON.parse(this.cookiesService.getCookies(TokensEnum.LockScreen)) || false;

    if (locked) {
      this.store.dispatch(new LockSystem(true));
    }

    this.activeUserIsLoggedInUser$
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (!locked && !value && this.hasPharmacy) {
          // if unlocked by someone other than logged in user,
          //  navigate to the dashboard.
          // This is a bit lazy, but I don't know how to access the
          //  the route guards for the current route in order to
          //  check it manually.
          this.router.navigateByUrl('/dashboard');
        } else if (this.hasPharmacy === false) {
          // if we don't have a pharmacy, but we have a user,
          // we're probably in the set-up process.
          this.router.navigateByUrl('/dashboard/set-up');
        }
      });

    this.currentPharmacy$.pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        if (data?.id) {
          this.hasPharmacy = true;
        } else {
          this.hasPharmacy = false;
        }
      });

    const timerIncrement = () => {
      idleMinutes += 1;

      if (idleMinutes === LOCK_TIME) {
        if (this.hasPharmacy) {
          // pharmacy was changed; reset the interval.
          this.store.dispatch(new LockSystem(true));
        } else {
          // no pharmacy selected, we're probably in the sign-up process -- don't lock the screen
          resetIdleMinutes();
        }
      } else if (idleMinutes === LOGOUT_TIME) {
        this.store.dispatch(new Logout());
      }
    };

    const resetIdleMinutes = () => {
      if (idleMinutes > 0) {
        idleMinutes = 0;
      }
    };

    setInterval(timerIncrement, 60 * 1000); // 1 min

    document.onmousemove = resetIdleMinutes;
    document.onkeydown = resetIdleMinutes;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.focusPin = true);

    this.lockScreen$.pipe(untilDestroyed(this))
      .subscribe((lock: boolean) => {
        if (lock) {
          this.initLockObserver();
        }
      });
  }

  unlockScreen(success: boolean): void {
    if (success) {
      this.store.dispatch(new LockSystem(false));
      this.removeLockObserver();
    }
  }

  initLockObserver(): void {
    if (this.intervalObserver) {
      clearInterval(this.intervalObserver);
    }

    setTimeout(() => {
      const dialogBackdropElement = document.querySelector('.dialog-background.blur') as HTMLElement;

      if (this.store.selectSnapshot(AuthState.lockScreen) === true && dialogBackdropElement) {
        this.intervalObserver = setInterval(() => {
          const styles = window.getComputedStyle ?
            getComputedStyle(dialogBackdropElement, null) : (dialogBackdropElement as any).currentStyle;

          if (styles && (styles.opacity !== 1 || styles.backdropFilter !== 'blur(2px)')) {
            dialogBackdropElement.style.opacity = '1';
            // tslint:disable-next-line
            dialogBackdropElement.style['backdropFilter'] = 'blur(2px)';
          }
        }, 500);
      }
    });
  }

  removeLockObserver(): void {
    if (this.intervalObserver) {
      clearInterval(this.intervalObserver);
    }
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;
    this.focusPin = true;
  }

  afterPinUpdated(success: boolean): void {
    if (success) {
      this.closeResetPinDialog();
    }
  }

  openResetPinDialog(): void {
    this.focusPin = false;
    this.showResetPinDialog = true;
  }
}
